<template>
  <div class="gl-reviews-page">

    <!-- Page header -->
    <div class="container-fluid">
      <div class="gl-page-header">
        <div>
          <div class="gl-breadcrumb">
            <router-link to="/">Home</router-link>
            <span class="gl-bc-sep">/</span>
            <span>Reviews</span>
          </div>
          <h1 class="gl-page-title">Customer Reviews</h1>
        </div>
        <button class="gl-refresh-btn" @click="refresh" :class="{ 'gl-refreshing': loading }">
          <svg class="gl-refresh-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
          Refresh
        </button>
      </div>
    </div>

    <div class="container-fluid">

      <!-- Stats strip -->
      <div class="gl-stats-strip" v-if="stats">
        <!-- Average rating -->
        <div class="gl-stat-rating">
          <span class="gl-rating-num">{{ stats.average_rating > 0 ? stats.average_rating.toFixed(1) : '—' }}</span>
          <div class="gl-rating-right">
            <div class="gl-stars-row">
              <span v-for="i in 5" :key="i" class="gl-star" :class="i <= Math.round(stats.average_rating) ? 'gl-star-filled' : 'gl-star-empty'">★</span>
            </div>
            <span class="gl-stat-lbl">Average Rating</span>
          </div>
        </div>

        <div class="gl-chip-div"></div>

        <!-- Total reviews -->
        <div class="gl-stat-chip">
          <span class="gl-chip-num">{{ stats.total }}</span>
          <span class="gl-stat-lbl">Total Reviews</span>
        </div>

        <div class="gl-chip-div"></div>

        <!-- Awaiting reply -->
        <div class="gl-stat-chip">
          <span class="gl-chip-num" :class="stats.awaiting_reply > 0 ? 'gl-chip-warn' : ''">{{ stats.awaiting_reply }}</span>
          <span class="gl-stat-lbl">Awaiting Reply</span>
        </div>

        <div class="gl-chip-div"></div>

        <!-- Distribution -->
        <div class="gl-distribution">
          <span class="gl-dist-label">Distribution</span>
          <div class="gl-dist-rows">
            <div v-for="n in [5,4,3,2,1]" :key="n" class="gl-dist-row">
              <span class="gl-dist-star">{{ n }}★</span>
              <div class="gl-dist-track">
                <div class="gl-dist-fill" :class="n >= 4 ? 'gl-dist-green' : n === 3 ? 'gl-dist-amber' : 'gl-dist-red'" :style="{ width: distributionPercent(n) + '%' }"></div>
              </div>
              <span class="gl-dist-count">{{ stats.distribution?.[n] || 0 }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Stats skeleton -->
      <div class="gl-stats-strip" v-else>
        <div class="gl-stat-chip" style="width:100px">
          <div class="gl-skel-num"></div>
          <div class="gl-skel-lbl"></div>
        </div>
        <div class="gl-chip-div"></div>
        <div class="gl-stat-chip" style="width:80px">
          <div class="gl-skel-num"></div>
          <div class="gl-skel-lbl"></div>
        </div>
        <div class="gl-chip-div"></div>
        <div class="gl-stat-chip" style="width:80px">
          <div class="gl-skel-num"></div>
          <div class="gl-skel-lbl"></div>
        </div>
      </div>

      <!-- Filters -->
      <div class="gl-filter-bar">
        <select class="gl-filter-select" v-model="filters.rating" @change="fetchReviews(1)">
          <option value="">All Ratings</option>
          <option v-for="n in [5,4,3,2,1]" :key="n" :value="n">{{ n }} Stars</option>
        </select>
        <select class="gl-filter-select" v-model="filters.outlet_id" @change="fetchReviews(1)">
          <option value="">All Branches</option>
          <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
        </select>
        <select class="gl-filter-select" v-model="filters.has_reply" @change="fetchReviews(1)">
          <option value="">Any Reply Status</option>
          <option value="false">Awaiting Reply</option>
          <option value="true">Replied</option>
        </select>
        <select class="gl-filter-select" v-model="filters.is_visible" @change="fetchReviews(1)">
          <option value="">Any Visibility</option>
          <option value="true">Visible</option>
          <option value="false">Hidden</option>
        </select>
        <div class="gl-date-group">
          <div class="gl-date-wrap">
            <svg class="gl-date-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            <input type="date" class="gl-date-input" v-model="filters.from_date">
          </div>
          <span class="gl-date-sep">–</span>
          <div class="gl-date-wrap">
            <svg class="gl-date-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            <input type="date" class="gl-date-input" v-model="filters.to_date">
          </div>
          <button class="gl-apply-btn" @click="fetchReviews(1)">Apply</button>
        </div>
        <button v-if="hasFilters" class="gl-clear-btn" @click="resetFilters">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          Clear
        </button>
      </div>

      <!-- Skeleton -->
      <template v-if="loading">
        <div v-for="i in 5" :key="i" class="gl-review-card gl-skel-card" :style="`animation-delay:${i*40}ms`">
          <div class="gl-rc-left">
            <div class="gl-skel-avatar"></div>
            <div style="flex:1;display:flex;flex-direction:column;gap:6px">
              <div class="gl-skel-line w40"></div>
              <div class="gl-skel-line w25"></div>
              <div class="gl-skel-line w60" style="margin-top:6px"></div>
              <div class="gl-skel-line w50"></div>
            </div>
          </div>
          <div class="gl-rc-right">
            <div class="gl-skel-line w30" style="height:22px"></div>
            <div class="gl-skel-line w20" style="margin-top:8px"></div>
          </div>
        </div>
      </template>

      <template v-else>
        <!-- Empty -->
        <div v-if="!reviews.length" class="gl-empty-card">
          <div class="gl-empty-icon">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
          </div>
          <p>No reviews match your filters.</p>
          <span v-if="hasFilters">Try clearing some filters</span>
        </div>

        <!-- Review cards -->
        <div
          v-for="r in reviews"
          :key="r.id"
          class="gl-review-card"
          :class="{ 'gl-rc-hidden': !r.is_visible }"
        >
          <div class="gl-rc-left">
            <!-- Customer -->
            <div class="gl-rc-avatar" :style="avatarStyle(r.user?.name)">{{ initials(r.user?.name) }}</div>
            <div class="gl-rc-body">
              <div class="gl-rc-name-row">
                <span class="gl-rc-name">{{ r.user?.name || 'Customer' }}</span>
                <span v-if="!r.is_visible" class="gl-hidden-badge">Hidden</span>
              </div>
              <div class="gl-rc-meta">
                <span v-if="r.outlet?.name" class="gl-meta-item">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                  {{ r.outlet.name }}
                </span>
                <span v-if="r.order?.order_number" class="gl-meta-item">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="20" y2="15"/><line x1="10" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="14" y2="21"/></svg>
                  {{ r.order.order_number }}
                </span>
                <span class="gl-meta-item">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                  {{ formatDate(r.created_at) }}
                </span>
              </div>

              <!-- Comment -->
              <p v-if="r.comment" class="gl-rc-comment">"{{ r.comment }}"</p>
              <p v-else class="gl-rc-no-comment">No written comment.</p>

              <!-- Reply -->
              <div v-if="r.reply" class="gl-reply-block">
                <div class="gl-reply-header">
                  <div class="gl-reply-label">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="15 10 20 15 15 20"/><path d="M4 4v7a4 4 0 004 4h12"/></svg>
                    Owner's Response
                  </div>
                  <span class="gl-reply-date">{{ formatDate(r.replied_at) }}</span>
                </div>
                <p class="gl-reply-text">{{ r.reply }}</p>
                <button class="gl-edit-reply-btn" @click="openReply(r)">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  Edit reply
                </button>
              </div>
              <div v-else class="gl-reply-cta">
                <button class="gl-reply-btn" @click="openReply(r)">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="15 10 20 15 15 20"/><path d="M4 4v7a4 4 0 004 4h12"/></svg>
                  Reply
                </button>
              </div>
            </div>
          </div>

          <!-- Rating + actions -->
          <div class="gl-rc-right">
            <div class="gl-rating-badge" :class="ratingBadgeClass(r.rating)">{{ r.rating }} ★</div>
            <div class="gl-stars-sm">
              <span v-for="i in 5" :key="i" class="gl-star-sm" :class="i <= r.rating ? 'gl-star-sm-filled' : 'gl-star-sm-empty'">★</span>
            </div>
            <button
              class="gl-vis-btn"
              :class="r.is_visible ? 'gl-vis-on' : 'gl-vis-off'"
              @click="toggleVisibility(r)"
              :title="r.is_visible ? 'Hide review' : 'Show review'"
            >
              <svg v-if="r.is_visible" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
              <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
            </button>
          </div>
        </div>

        <!-- Pagination -->
        <div v-if="pagination.last_page > 1" class="gl-pagination">
          <span class="gl-page-info">Page {{ pagination.current_page }} of {{ pagination.last_page }} · {{ pagination.total }} reviews</span>
          <div class="gl-page-btns">
            <button class="gl-page-btn" :disabled="pagination.current_page === 1" @click="fetchReviews(pagination.current_page - 1)">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="15 18 9 12 15 6"/></svg>
              Prev
            </button>
            <button class="gl-page-btn" :disabled="pagination.current_page === pagination.last_page" @click="fetchReviews(pagination.current_page + 1)">
              Next
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
          </div>
        </div>
      </template>
    </div>

    <!-- Reply Modal -->
    <div v-if="replyModalOpen" class="gl-modal-backdrop" @click.self="closeReply">
      <div class="gl-modal">
        <div class="gl-modal-header">
          <h3 class="gl-modal-title">{{ replyTarget?.reply ? 'Edit Reply' : 'Reply to Review' }}</h3>
          <button class="gl-modal-close" @click="closeReply">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div class="gl-modal-body">
          <!-- Review preview -->
          <div v-if="replyTarget" class="gl-modal-preview">
            <div class="gl-mp-stars">
              <span v-for="i in 5" :key="i" class="gl-star-sm" :class="i <= replyTarget.rating ? 'gl-star-sm-filled' : 'gl-star-sm-empty'">★</span>
              <strong class="gl-mp-name">{{ replyTarget.user?.name }}</strong>
            </div>
            <p class="gl-mp-comment">{{ replyTarget.comment || 'No comment.' }}</p>
          </div>
          <label class="gl-modal-label">Your Response <span class="gl-required">*</span></label>
          <textarea
            class="gl-modal-textarea"
            v-model="replyText"
            rows="4"
            maxlength="1000"
            placeholder="Write a thoughtful response…"
          ></textarea>
          <div class="gl-char-count">{{ replyText.length }} / 1000</div>
        </div>
        <div class="gl-modal-footer">
          <button class="gl-btn-cancel" @click="closeReply">Cancel</button>
          <button class="gl-btn-submit" @click="submitReply" :disabled="replying || !replyText.trim()">
            <svg v-if="!replying" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
            <svg v-else class="gl-spin" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
            {{ replying ? 'Saving…' : 'Publish Reply' }}
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { ownerReviewApi, ownerBranchApi } from '@/services/ownerApi';

const AVATAR_COLORS = [
  { bg: '#dff0ec', color: '#900C27' },
  { bg: '#dbeafe', color: '#2563eb' },
  { bg: '#fef3c7', color: '#d97706' },
  { bg: '#ede9fe', color: '#7c3aed' },
  { bg: '#fce7f3', color: '#be185d' },
];

export default {
  name: 'ReviewList',

  data() {
    return {
      loading: true,
      reviews: [],
      branches: [],
      stats: null,
      filters: { rating: '', outlet_id: '', has_reply: '', is_visible: '', from_date: '', to_date: '' },
      pagination: { current_page: 1, last_page: 1, per_page: 20, total: 0 },
      replyModalOpen: false,
      replyTarget: null,
      replyText: '',
      replying: false,
    };
  },

  computed: {
    hasFilters() {
      return Object.values(this.filters).some(v => v !== '');
    }
  },

  mounted() {
    Promise.all([this.fetchBranches(), this.fetchStats()]);
    this.fetchReviews();
  },

  methods: {
    refresh() {
      this.fetchStats();
      this.fetchReviews(this.pagination.current_page);
    },

    async fetchBranches() {
      try {
        const r = await ownerBranchApi.list({ per_page: 100 });
        this.branches = r.data.data;
      } catch {}
    },

    async fetchStats() {
      try {
        const r = await ownerReviewApi.stats();
        this.stats = r.data.data;
      } catch {}
    },

    async fetchReviews(page = 1) {
      this.loading = true;
      try {
        const params = { page, ...this.cleanFilters() };
        const r = await ownerReviewApi.list(params);
        this.reviews = r.data.data;
        if (r.data.meta) this.pagination = r.data.meta;
      } catch (e) {
        console.error('Failed to load reviews', e);
      } finally {
        this.loading = false;
      }
    },

    cleanFilters() {
      const out = {};
      Object.keys(this.filters).forEach(k => {
        if (this.filters[k] !== '' && this.filters[k] !== null) out[k] = this.filters[k];
      });
      return out;
    },

    resetFilters() {
      this.filters = { rating: '', outlet_id: '', has_reply: '', is_visible: '', from_date: '', to_date: '' };
      this.fetchReviews(1);
    },

    distributionPercent(n) {
      if (!this.stats || !this.stats.total) return 0;
      return Math.round(((this.stats.distribution?.[n] || 0) / this.stats.total) * 100);
    },

    initials(name) {
      if (!name) return '?';
      return name.split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase();
    },

    avatarStyle(name) {
      const c = AVATAR_COLORS[(name || '?').charCodeAt(0) % AVATAR_COLORS.length];
      return { background: c.bg, color: c.color };
    },

    formatDate(v) {
      if (!v) return '';
      return new Date(v).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' });
    },

    ratingBadgeClass(r) {
      if (r >= 4) return 'gl-rating-good';
      if (r === 3) return 'gl-rating-mid';
      return 'gl-rating-bad';
    },

    openReply(review) {
      this.replyTarget = review;
      this.replyText = review.reply || '';
      this.replyModalOpen = true;
    },

    closeReply() {
      this.replyModalOpen = false;
      this.replyTarget = null;
      this.replyText = '';
    },

    async submitReply() {
      if (!this.replyTarget || !this.replyText.trim()) return;
      this.replying = true;
      try {
        const r = await ownerReviewApi.reply(this.replyTarget.id, this.replyText.trim());
        const updated = r.data.data;
        const idx = this.reviews.findIndex(x => x.id === updated.id);
        if (idx > -1) this.reviews[idx] = { ...this.reviews[idx], ...updated };
        this.closeReply();
        this.fetchStats();
      } catch (e) {
        alert(e.response?.data?.message || 'Failed to save reply');
      } finally {
        this.replying = false;
      }
    },

    async toggleVisibility(review) {
      try {
        const r = await ownerReviewApi.toggleVisibility(review.id);
        review.is_visible = r.data.data.is_visible;
      } catch (e) {
        alert(e.response?.data?.message || 'Failed to toggle');
      }
    },
  },
};
</script>

<style scoped>
/* ── Page header ── */
.gl-reviews-page { padding-bottom: 40px; }
.gl-page-header { display: flex; align-items: center; justify-content: space-between; padding: 20px 0 10px; }
.gl-breadcrumb { font-size: 12px; color: #9ca3af; margin-bottom: 4px; }
.gl-breadcrumb a { color: #9ca3af; text-decoration: none; }
.gl-breadcrumb a:hover { color: #374151; }
.gl-bc-sep { margin: 0 6px; }
.gl-page-title { font-size: 22px; font-weight: 700; color: #111827; margin: 0; letter-spacing: -0.3px; }
.gl-refresh-btn {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 8px 16px; border: 1.5px solid #e8eaf0; border-radius: 10px;
  background: #fff; color: #374151; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.15s;
}
.gl-refresh-btn:hover { border-color: #900C27; color: #900C27; background: #f0f5f4; }
.gl-refresh-icon { transition: transform 0.15s; }
.gl-refreshing .gl-refresh-icon { animation: gl-spin 1s linear infinite; }

/* ── Stats strip ── */
.gl-stats-strip {
  display: flex; align-items: center; flex-wrap: wrap; gap: 4px;
  background: #fff; border: 1.5px solid #f0f1f3; border-radius: 14px;
  padding: 12px 20px; margin-bottom: 12px;
}
.gl-stat-rating { display: flex; align-items: center; gap: 10px; padding: 4px 14px 4px 0; }
.gl-rating-num { font-size: 34px; font-weight: 900; color: #111827; line-height: 1; letter-spacing: -1.5px; }
.gl-rating-right { display: flex; flex-direction: column; gap: 3px; }
.gl-stars-row { display: flex; gap: 2px; }
.gl-star { font-size: 15px; line-height: 1; }
.gl-star-filled { color: #f59e0b; }
.gl-star-empty  { color: #e5e7eb; }
.gl-stat-lbl { font-size: 10.5px; color: #9ca3af; font-weight: 500; white-space: nowrap; }
.gl-stat-chip { display: flex; flex-direction: column; align-items: flex-start; padding: 4px 14px 4px 0; }
.gl-chip-num { font-size: 22px; font-weight: 800; color: #111827; line-height: 1.1; min-height: 26px; letter-spacing: -0.5px; }
.gl-chip-warn { color: #d97706; }
.gl-chip-div { width: 1px; height: 34px; background: #f0f1f3; margin: 0 10px; flex-shrink: 0; }

/* Distribution */
.gl-distribution { display: flex; flex-direction: column; gap: 4px; margin-left: auto; min-width: 200px; }
.gl-dist-label { font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.07em; color: #b0b7c3; margin-bottom: 2px; }
.gl-dist-row { display: flex; align-items: center; gap: 8px; }
.gl-dist-star { font-size: 10.5px; color: #9ca3af; width: 22px; white-space: nowrap; }
.gl-dist-track { flex: 1; height: 5px; background: #f3f4f6; border-radius: 3px; overflow: hidden; }
.gl-dist-fill { height: 100%; border-radius: 3px; min-width: 2px; transition: width 0.4s ease; }
.gl-dist-green { background: #900C27; }
.gl-dist-amber { background: #f59e0b; }
.gl-dist-red   { background: #ef4444; }
.gl-dist-count { font-size: 10.5px; color: #9ca3af; width: 14px; text-align: right; }

/* Skeleton nums */
.gl-skel-num { width: 40px; height: 22px; background: #f0f1f3; border-radius: 6px; animation: gl-pulse 1.4s ease-in-out infinite; }
.gl-skel-lbl { width: 60px; height: 10px; background: #f0f1f3; border-radius: 5px; margin-top: 5px; animation: gl-pulse 1.4s ease-in-out infinite; }

/* ── Filter bar ── */
.gl-filter-bar {
  display: flex; align-items: center; flex-wrap: wrap; gap: 8px;
  background: transparent; border: none; padding: 0;
  margin-bottom: 14px;
}
.gl-filter-select {
  border: 1.5px solid #e8eaf0; border-radius: 9px; padding: 6px 10px;
  font-size: 13px; color: #374151; background: #fff; outline: none; cursor: pointer; transition: border-color 0.15s;
}
.gl-filter-select:focus { border-color: #900C27; }
.gl-date-group { display: flex; align-items: center; gap: 6px; margin-left: auto; flex-wrap: wrap; }
.gl-date-sep { font-size: 13px; color: #9ca3af; }
.gl-date-wrap { position: relative; display: inline-flex; align-items: center; }
.gl-date-icon { position: absolute; left: 8px; color: #9ca3af; pointer-events: none; }
.gl-date-input {
  border: 1.5px solid #e8eaf0; border-radius: 9px; padding: 6px 8px 6px 26px;
  font-size: 12.5px; color: #374151; background: #fff; outline: none; width: 140px; transition: border-color 0.15s;
}
.gl-date-input:focus { border-color: #900C27; }
.gl-apply-btn {
  padding: 6px 16px; border: none; border-radius: 9px;
  background: #900C27; color: #fff; font-size: 13px; font-weight: 600; cursor: pointer; transition: background 0.15s;
}
.gl-apply-btn:hover { background: #1a5248; }
.gl-clear-btn {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 6px 12px; border: 1.5px solid #fecaca; border-radius: 8px;
  background: #fff5f5; color: #dc2626; font-size: 12px; font-weight: 600; cursor: pointer; transition: background 0.15s;
}
.gl-clear-btn:hover { background: #fee2e2; }

/* ── Review cards ── */
.gl-review-card {
  display: flex; align-items: flex-start; gap: 16px;
  background: #fff; border: 1.5px solid #f0f1f3; border-radius: 14px;
  padding: 18px 20px; margin-bottom: 10px; transition: box-shadow 0.15s;
  animation: gl-fadein 0.25s ease;
}
.gl-review-card:hover { box-shadow: 0 2px 12px rgba(0,0,0,0.05); }
.gl-rc-hidden { opacity: 0.5; }
.gl-rc-left { display: flex; align-items: flex-start; gap: 12px; flex: 1; min-width: 0; }
.gl-rc-avatar {
  width: 38px; height: 38px; border-radius: 50%; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 13px; font-weight: 700;
}
.gl-rc-body { flex: 1; min-width: 0; }
.gl-rc-name-row { display: flex; align-items: center; gap: 8px; margin-bottom: 3px; }
.gl-rc-name { font-size: 14px; font-weight: 700; color: #111827; }
.gl-hidden-badge { display: inline-block; padding: 2px 7px; border-radius: 4px; background: #f3f4f6; color: #9ca3af; font-size: 10.5px; font-weight: 600; }
.gl-rc-meta { display: flex; flex-wrap: wrap; gap: 10px; margin-bottom: 10px; }
.gl-meta-item { display: inline-flex; align-items: center; gap: 4px; font-size: 11.5px; color: #9ca3af; }
.gl-meta-item svg { flex-shrink: 0; }

.gl-rc-comment { font-size: 13.5px; color: #374151; font-style: italic; line-height: 1.55; margin: 0 0 10px; }
.gl-rc-no-comment { font-size: 13px; color: #b0b7c3; font-style: italic; margin: 0 0 10px; }

/* Reply block */
.gl-reply-block {
  border-left: 3px solid #900C27; padding-left: 12px;
  margin-top: 6px; background: #f8fafa; border-radius: 0 8px 8px 0;
  padding: 10px 14px 10px 12px;
}
.gl-reply-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 5px; }
.gl-reply-label { display: inline-flex; align-items: center; gap: 5px; font-size: 12px; font-weight: 700; color: #900C27; }
.gl-reply-date { font-size: 11px; color: #9ca3af; }
.gl-reply-text { font-size: 13px; color: #374151; line-height: 1.5; margin: 0 0 6px; }
.gl-edit-reply-btn {
  display: inline-flex; align-items: center; gap: 5px;
  background: none; border: none; padding: 0; cursor: pointer;
  font-size: 11.5px; color: #9ca3af; font-weight: 500; transition: color 0.15s;
}
.gl-edit-reply-btn:hover { color: #900C27; }

.gl-reply-cta { margin-top: 8px; }
.gl-reply-btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 14px; border: 1.5px solid #c8e2da; border-radius: 8px;
  background: #f0f5f4; color: #900C27; font-size: 12.5px; font-weight: 600;
  cursor: pointer; transition: all 0.15s;
}
.gl-reply-btn:hover { background: #dff0ec; border-color: #900C27; }

/* Rating + right col */
.gl-rc-right { display: flex; flex-direction: column; align-items: flex-end; gap: 6px; flex-shrink: 0; }
.gl-rating-badge { display: inline-block; padding: 4px 10px; border-radius: 7px; font-size: 13px; font-weight: 800; white-space: nowrap; }
.gl-rating-good { background: #dff0ec; color: #900C27; }
.gl-rating-mid  { background: #fef3c7; color: #d97706; }
.gl-rating-bad  { background: #fee2e2; color: #dc2626; }
.gl-stars-sm { display: flex; gap: 1px; }
.gl-star-sm { font-size: 13px; }
.gl-star-sm-filled { color: #f59e0b; }
.gl-star-sm-empty  { color: #e5e7eb; }

.gl-vis-btn {
  width: 30px; height: 30px; border-radius: 8px; border: 1.5px solid #e8eaf0;
  display: flex; align-items: center; justify-content: center;
  background: #fff; cursor: pointer; transition: all 0.15s;
}
.gl-vis-on { color: #9ca3af; }
.gl-vis-on:hover { border-color: #fecaca; color: #dc2626; background: #fff5f5; }
.gl-vis-off { border-color: #d1d5db; color: #d1d5db; }
.gl-vis-off:hover { border-color: #900C27; color: #900C27; background: #f0f5f4; }

/* ── Empty ── */
.gl-empty-card {
  display: flex; flex-direction: column; align-items: center; gap: 8px;
  background: #fff; border: 1.5px solid #f0f1f3; border-radius: 14px;
  padding: 52px 20px;
}
.gl-empty-icon { width: 52px; height: 52px; border-radius: 50%; background: #f3f4f6; display: flex; align-items: center; justify-content: center; color: #d1d5db; }
.gl-empty-card p { font-size: 14px; color: #6b7280; font-weight: 600; margin: 0; }
.gl-empty-card span { font-size: 12.5px; color: #b0b7c3; }

/* ── Pagination ── */
.gl-pagination { display: flex; align-items: center; justify-content: space-between; padding: 12px 0 4px; }
.gl-page-info { font-size: 12.5px; color: #9ca3af; }
.gl-page-btns { display: flex; align-items: center; gap: 6px; }
.gl-page-btn {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 6px 14px; border: 1.5px solid #e8eaf0; border-radius: 8px;
  background: #fff; color: #374151; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.15s;
}
.gl-page-btn:hover:not(:disabled) { border-color: #900C27; color: #900C27; background: #f0f5f4; }
.gl-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }

/* ── Skeleton ── */
.gl-skel-card { animation: gl-pulse 1.4s ease-in-out infinite; }
.gl-rc-left { }
.gl-skel-avatar { width: 38px; height: 38px; border-radius: 50%; background: #f0f1f3; flex-shrink: 0; }
.gl-skel-line { height: 11px; background: #f0f1f3; border-radius: 5px; }
.w25 { width: 25%; } .w30 { width: 30%; } .w40 { width: 40%; } .w50 { width: 50%; } .w60 { width: 60%; }

/* ── Modal ── */
.gl-modal-backdrop {
  position: fixed; inset: 0; background: rgba(0,0,0,0.4); backdrop-filter: blur(2px);
  z-index: 1050; display: flex; align-items: center; justify-content: center; padding: 20px;
}
.gl-modal {
  background: #fff; border-radius: 16px; width: 100%; max-width: 480px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.15); animation: gl-fadein 0.2s ease;
}
.gl-modal-header { display: flex; align-items: center; justify-content: space-between; padding: 18px 22px 0; }
.gl-modal-title { font-size: 16px; font-weight: 700; color: #111827; margin: 0; }
.gl-modal-close {
  width: 28px; height: 28px; border-radius: 7px; border: 1.5px solid #f0f1f3;
  background: #fff; display: flex; align-items: center; justify-content: center;
  color: #9ca3af; cursor: pointer; transition: all 0.15s;
}
.gl-modal-close:hover { border-color: #e8eaf0; color: #374151; }
.gl-modal-body { padding: 16px 22px; }
.gl-modal-preview {
  background: #f8f9fa; border-radius: 10px; padding: 12px 14px; margin-bottom: 14px;
}
.gl-mp-stars { display: flex; align-items: center; gap: 6px; margin-bottom: 6px; }
.gl-mp-name { font-size: 12.5px; color: #374151; font-weight: 600; }
.gl-mp-comment { font-size: 13px; color: #6b7280; font-style: italic; margin: 0; }
.gl-modal-label { display: block; font-size: 12.5px; font-weight: 600; color: #374151; margin-bottom: 6px; }
.gl-required { color: #dc2626; }
.gl-modal-textarea {
  width: 100%; border: 1.5px solid #e8eaf0; border-radius: 10px;
  padding: 10px 12px; font-size: 13.5px; color: #111827; line-height: 1.55;
  resize: vertical; outline: none; box-sizing: border-box; transition: border-color 0.15s;
  font-family: inherit;
}
.gl-modal-textarea:focus { border-color: #900C27; }
.gl-char-count { text-align: right; font-size: 11.5px; color: #b0b7c3; margin-top: 4px; }
.gl-modal-footer { display: flex; justify-content: flex-end; gap: 8px; padding: 0 22px 18px; }
.gl-btn-cancel {
  padding: 8px 18px; border: 1.5px solid #e8eaf0; border-radius: 9px;
  background: #fff; color: #374151; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.15s;
}
.gl-btn-cancel:hover { background: #f3f4f6; }
.gl-btn-submit {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 20px; border: none; border-radius: 9px;
  background: #900C27; color: #fff; font-size: 13px; font-weight: 700; cursor: pointer; transition: background 0.15s;
}
.gl-btn-submit:hover:not(:disabled) { background: #1a5248; }
.gl-btn-submit:disabled { opacity: 0.6; cursor: not-allowed; }

/* ── Mobile ── */
@media (max-width: 767px) {
  .gl-page-header { padding: 14px 0 8px; gap: 8px; }
  .gl-page-title { font-size: 18px; }
  .gl-breadcrumb { display: none; }

  /* Stats strip: keep card, tighten, push distribution to own row */
  .gl-stats-strip { padding: 12px 14px; gap: 4px; }
  .gl-rating-num { font-size: 26px; }
  .gl-chip-num { font-size: 18px; }
  .gl-chip-div { height: 26px; margin: 0 6px; }
  .gl-distribution {
    margin-left: 0; min-width: 100%; margin-top: 8px;
    border-top: 1px solid #f0f1f3; padding-top: 8px;
  }

  /* Filter bar: remove card */
  .gl-filter-bar { background: transparent; border: none; padding: 0; margin-bottom: 10px; }
  .gl-filter-select { flex: 1; min-width: 130px; }
  .gl-date-group { margin-left: 0; width: 100%; flex-wrap: wrap; justify-content: flex-end; }
  .gl-date-wrap { flex: 1; }
  .gl-date-input { width: 100%; box-sizing: border-box; }

  /* Review cards */
  .gl-review-card { padding: 14px; gap: 10px; }
  .gl-rc-right { gap: 4px; }

  /* Pagination */
  .gl-pagination { flex-direction: column; align-items: center; gap: 8px; }

  /* Modal */
  .gl-modal-backdrop { padding: 68px 16px 16px; align-items: center; }
  .gl-modal { border-radius: 16px; max-height: calc(100vh - 84px); overflow-y: auto; }
}

/* ── Animations ── */
@keyframes gl-fadein { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
@keyframes gl-pulse  { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
@keyframes gl-spin   { to { transform: rotate(360deg); } }
.gl-spin { animation: gl-spin 0.8s linear infinite; }
</style>

<style>
/* Dark mode */
body.dark-only .gl-page-title { color: #f3f4f6; }
body.dark-only .gl-breadcrumb, body.dark-only .gl-breadcrumb a { color: #6b7280; }
body.dark-only .gl-refresh-btn { background: #1f2937; border-color: #374151; color: #d1d5db; }
body.dark-only .gl-refresh-btn:hover { border-color: #900C27; color: #a7c4be; background: #1a3330; }

body.dark-only .gl-stats-strip { background: #1f2937; border-color: #374151; }
body.dark-only .gl-rating-num { color: #f9fafb; }
body.dark-only .gl-chip-num { color: #f9fafb; }
body.dark-only .gl-stat-lbl { color: #6b7280; }
body.dark-only .gl-chip-div { background: #374151; }
body.dark-only .gl-star-empty { color: #374151; }
body.dark-only .gl-dist-track { background: #374151; }
body.dark-only .gl-dist-star, body.dark-only .gl-dist-count { color: #6b7280; }

body.dark-only .gl-filter-select { background: #111827; border-color: #374151; color: #d1d5db; }
body.dark-only .gl-date-input { background: #111827; border-color: #374151; color: #d1d5db; }

body.dark-only .gl-review-card { background: #1f2937; border-color: #374151; }
body.dark-only .gl-rc-name { color: #f9fafb; }
body.dark-only .gl-meta-item { color: #6b7280; }
body.dark-only .gl-rc-comment { color: #d1d5db; }
body.dark-only .gl-rc-no-comment { color: #4b5563; }
body.dark-only .gl-reply-block { background: #1a3330; border-left-color: #900C27; }
body.dark-only .gl-reply-text { color: #d1d5db; }
body.dark-only .gl-reply-date { color: #6b7280; }
body.dark-only .gl-reply-btn { background: #1a3330; border-color: #2d5249; color: #a7c4be; }
body.dark-only .gl-reply-btn:hover { background: #dff0ec1a; border-color: #900C27; }
body.dark-only .gl-vis-btn { background: #1f2937; border-color: #374151; }
body.dark-only .gl-star-sm-empty { color: #374151; }
body.dark-only .gl-hidden-badge { background: #374151; color: #6b7280; }

body.dark-only .gl-empty-card { background: #1f2937; border-color: #374151; }
body.dark-only .gl-empty-icon { background: #374151; color: #6b7280; }

body.dark-only .gl-page-btn { background: #1f2937; border-color: #374151; color: #d1d5db; }
body.dark-only .gl-page-btn:hover:not(:disabled) { border-color: #900C27; color: #a7c4be; }
body.dark-only .gl-page-info { color: #6b7280; }

body.dark-only .gl-modal { background: #1f2937; }
body.dark-only .gl-modal-title { color: #f9fafb; }
body.dark-only .gl-modal-close { background: #1f2937; border-color: #374151; color: #6b7280; }
body.dark-only .gl-modal-preview { background: #111827; }
body.dark-only .gl-mp-comment { color: #9ca3af; }
body.dark-only .gl-mp-name { color: #d1d5db; }
body.dark-only .gl-modal-label { color: #d1d5db; }
body.dark-only .gl-modal-textarea { background: #111827; border-color: #374151; color: #f9fafb; }
body.dark-only .gl-btn-cancel { background: #1f2937; border-color: #374151; color: #d1d5db; }
body.dark-only .gl-btn-cancel:hover { background: #374151; }
body.dark-only .gl-skel-avatar, body.dark-only .gl-skel-line, body.dark-only .gl-skel-num, body.dark-only .gl-skel-lbl { background: #374151; }

@media (max-width: 767px) {
  body.dark-only .gl-distribution { border-top-color: #374151; }
  body.dark-only .gl-filter-bar { background: transparent !important; border: none !important; }
}
</style>
